import IPassengerPhonNumberViewModel from '@/components/Passenger/models/IPassengerPhoneNumberViewModel';
import PassengerWithSms from '@/components/Passenger/models/PassengerWithSms';
import RideitInvitation, { RideitInvitationStatus } from '@/models/Rideit/RideitInvitation';
import { RideitPassengerToken } from '@/models/Rideit/RideitPassengerToken';
import SmsMessage, { SmsStatus } from '@/models/SmsMessage';
import moment from 'moment';

export interface IInvitationWithSms {
    invitation: RideitInvitation;
    smsMessage: SmsMessage;
}

export interface IRideitInvitationTableItem {
    name: string;
    destination: string;
    tags: string;
    invitations: RideitInvitation[];
    tokens: RideitPassengerToken[];
    phone: string;
    newTokensAmount: number;
    passenger: PassengerWithSms;
    selected: boolean;
}

export enum RideitInvitationWithSmsStatus {
    Pending = 'Pending',
    Sent = 'Sent',
    Failed = 'Failed',
    Accepted = 'Accepted',
    Expired = 'Expired',
    Revoked = 'Revoked',
}

export function getInvitationStatus(invitation: RideitInvitation, smsMessage: SmsMessage) {
    if (invitation?.status === RideitInvitationStatus.Accepted) {
        return RideitInvitationWithSmsStatus.Accepted;
    }
    if (invitation?.status === RideitInvitationStatus.Revoked) {
        return RideitInvitationWithSmsStatus.Revoked;
    }
    if (invitation?.status === RideitInvitationStatus.Expired) {
        return RideitInvitationWithSmsStatus.Expired;
    }
    if (smsMessage?.status === SmsStatus.Failed) {
        return RideitInvitationWithSmsStatus.Failed;
    }
    if (smsMessage?.status === SmsStatus.Received) {
        return RideitInvitationWithSmsStatus.Sent;
    }
    return RideitInvitationWithSmsStatus.Pending;
}

export function getLatestInvitationPerPhoneNumber(invitations: RideitInvitation[], smsMessages: SmsMessage[]): IInvitationWithSms[] {
    // Group all invitations with their sms message
    const allItems =  invitations
        .map(invitation => {
            const smsMessage = smsMessages.find(sms => sms.rideitInvitationId === invitation.id);
            if (smsMessage) {
                return { invitation, smsMessage }
            }
            return null;
        })
        // There should always be an sms message for each invitation, but lets be safe.
        .filter(i => i !== null)  as IInvitationWithSms[];

    //Only get the latest invitation for each sms message phone number.
    return Array.from(allItems.reduce((map, item) => {
        // If there are multiple messages to the same number, find the previous map entry for it.
        const existing = map.get(item.smsMessage.to);

        if (!existing) {
            // If we don't have an entry, add it and move on.
            map.set(item.smsMessage.to, item);
            return map;
        }

        // Get times for comparison
        const messageTime = moment(item.smsMessage.statusUpdatedAt);
        const existingMessageTime = moment(existing.smsMessage.statusUpdatedAt);

        if (messageTime.isAfter(existingMessageTime)) {
            // current message is newer, replace the old entry
            map.set(item.smsMessage.to, item);
        }

        return map;
    }, new Map<string, IInvitationWithSms>()).values());
}

